import { useMemo } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { usd } from "@/lib/api";
import { fmtDate } from "@/components/DatePicker";

const MONEY = new Set(["Gross", "Federal", "State", "SS", "Medicare", "401(k)", "Health", "Net", "Taxes", "Deductions", "Social Security", "Total", "Total deductions (incl. tax)", "OT pay", "Gross YTD", "Net YTD", "Total paid", "Amount", "Avg gross / employee"]);
const PREF = ["Gross", "Net", "Taxes", "Federal", "State", "Total", "OT pay", "Total paid", "Gross YTD", "Net YTD", "Amount", "401(k)", "Health", "Deductions"];
const COLORS = ["#2563EB", "#0f172a", "#f59e0b", "#10b981"];
const TIME = new Set(["Month", "Pay date"]);

export function ReportChart({ rep, testid = "report-chart" }) {
  const cfg = useMemo(() => {
    if (!rep || !rep.rows?.length) return null;
    const cols = rep.columns;
    const xi = cols.findIndex((c) => TIME.has(c)) >= 0 ? cols.findIndex((c) => TIME.has(c)) : cols.findIndex((c) => !MONEY.has(c) && c !== "Client" && c !== "Check #");
    if (xi < 0) return null;
    const series = PREF.filter((p) => cols.includes(p) && MONEY.has(p)).slice(0, 3);
    if (!series.length) return null;
    const trend = TIME.has(cols[xi]);
    const agg = new Map();
    for (const r of rep.rows) {
      const k = String(r[xi]); const row = agg.get(k) || { x: k };
      series.forEach((s) => { row[s] = (row[s] || 0) + Number(r[cols.indexOf(s)] || 0); });
      agg.set(k, row);
    }
    let data = [...agg.values()];
    if (trend) data.sort((a, b) => a.x.localeCompare(b.x)); else data = data.sort((a, b) => (b[series[0]] || 0) - (a[series[0]] || 0)).slice(0, 12);
    data = data.map((d) => ({ ...d, label: trend ? (cols[xi] === "Month" ? `${d.x.slice(5, 7)}/${d.x.slice(0, 4)}` : fmtDate(d.x)) : d.x.length > 14 ? d.x.slice(0, 13) + "…" : d.x }));
    return { data, series, trend, xLabel: cols[xi] };
  }, [rep]);
  if (!cfg) return null;
  const Chart = cfg.trend ? LineChart : BarChart;
  return (
    <div className="rounded-lg border bg-muted/30 p-3" data-testid={testid}>
      <div className="text-xs text-muted-foreground mb-1">{cfg.trend ? "Trend" : "Top values"} by {cfg.xLabel}</div>
      <ResponsiveContainer width="100%" height={220}>
        <Chart data={cfg.data} margin={{ top: 4, right: 12, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={cfg.data.length > 8 ? -25 : 0} height={cfg.data.length > 8 ? 50 : 30} textAnchor={cfg.data.length > 8 ? "end" : "middle"} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={52} />
          <Tooltip formatter={(v) => usd(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          {cfg.series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {cfg.series.map((s, i) => cfg.trend
            ? <Line key={s} type="monotone" dataKey={s} stroke={COLORS[i]} strokeWidth={2.5} dot={{ r: 3 }} />
            : <Bar key={s} dataKey={s} fill={COLORS[i]} radius={[4, 4, 0, 0]} />)}
        </Chart>
      </ResponsiveContainer>
    </div>
  );
}
